/* @flow */

import { MetricsStreamType, type NIELSEN_METRICS_PARAMS, type NIELSEN_METRICS_PROPERTIES } from '../../libs/netgemLibrary/v8/types/Channel';
import DidomiWrapper from '../consent/didomi';
import { SECONDS_PER_DAY } from '../dateTime/Format';
import { logWarning } from '../debug/debug';
import { MediametrieDiffusionMode, type NielsenSessionMetadata } from './types';

/*
 * Switching from LIVE to TIMESHIFTING when reader head is more than 60s behind live and switching back to LIVE when it's less than 50s behind live (in seconds)
 * See 3.1.1 in "Mesure 4 Ecrans - Plan de marquage eStat 3 niveaux TV - TF1 - V3.0 - 11042016.pdf"
 * (same behavior for Estat and Nielsen)
 */
const DOWNWARD_TIMESHIFT_THRESHOLD = 60;
const UPWARD_TIMESHIFT_THRESHOLD = 50;

// Playhead position is polled every second (in ms)
const POSITION_POLLING_INTERVAL = 1_000;

const NIELSEN_APP_ID = process.env.REACT_APP_NIELSEN_APPID;

export default class Nielsen {
  static getPlayerPosition: () => number;
  static initialMetadata: NIELSEN_METRICS_PARAMS | null = null;
  static isPlayerBuffering: () => boolean;
  static metadata: NIELSEN_METRICS_PARAMS | null = null;
  static pollingTimer: IntervalID | null = null;
  // Nielsen SDK instance
  static sdkInstance: NielsenSDKInstance | null = null;

  static get isEnabled(): boolean {
    return Nielsen.sdkInstance !== null;
  }

  static initialize: () => void = () => {
    if (!NIELSEN_APP_ID) {
      logWarning('Cannot initialize Nielsen SDK because no app Id has been provided');
      return;
    }

    const optIn = DidomiWrapper.isMediametrieEnabled();

    Nielsen.sdkInstance = NOLBUNDLE.nlsQ(process.env.REACT_APP_NIELSEN_APPID, 'TVnamevideoSdkInstance', {
      enableFpid: optIn.toString(),
      optout: (!optIn).toString(),
    });

    window.addEventListener('beforeunload', () => {
      Nielsen.notifyStop();
    });
  };

  static prepareSession(metrics: NIELSEN_METRICS_PROPERTIES | null, getPlayerPosition: () => number, isPlayerBuffering: () => boolean, sessionMetadata: NielsenSessionMetadata) {
    if (metrics === null) {
      // No metrics means no Mediametrie (metadata is reset in case of a previous unfinished play)
      Nielsen.metadata = null;
      return;
    }

    const { params, type, vcid } = metrics;

    if (!Nielsen.isEnabled) {
      return;
    }

    Nielsen.getPlayerPosition = getPlayerPosition;
    Nielsen.isPlayerBuffering = isPlayerBuffering;
    const isLive = type === MetricsStreamType.Live;

    const { program, title } = sessionMetadata;

    const metadata: NIELSEN_METRICS_PARAMS = {
      cli_md: isLive ? (MediametrieDiffusionMode.Live as string) : (MediametrieDiffusionMode.Vod as string),
      islivestn: isLive ? 'y' : 'n',
      program: program || '-',
      subbrand: vcid,
      title: title || '-',
      type: 'content',
    };

    const keys: Array<keyof typeof params> = [
      'assetid',
      'cli_ch',
      'cli_cn',
      'cli_md',
      'islivestn',
      'length',
      'nol_p0',
      'nol_p1',
      'nol_p2',
      'nol_p3',
      'nol_p4',
      'nol_p5',
      'nol_p6',
      'nol_p7',
      'nol_p8',
      'nol_p9',
      'nol_p10',
      'nol_p11',
      'nol_p12',
      'nol_p13',
      'nol_p14',
      'nol_p15',
      'nol_p16',
      'nol_p17',
      'nol_p18',
      'nol_p19',
      'program',
      'subbrand',
      'title',
    ];

    keys.forEach((key) => {
      const value = params[key];
      if (typeof value === 'string') {
        metadata[key] = value;
      }
    });

    if (metadata.length === undefined && isLive) {
      metadata.length = SECONDS_PER_DAY.toString();
    }

    Nielsen.metadata = metadata;
    Nielsen.initialMetadata = { ...metadata };

    Nielsen.sendEvent('loadMetadata', Nielsen.metadata);
  }

  static startPolling: () => void = () => {
    if (Nielsen.pollingTimer) {
      // Timer already set
      return;
    }

    /*
     * From Nielsen doc:
     * Pass playhead position every second during playback,
     * the value passed should match the broadcast time for live channel.
     */
    const sendPosition = () => {
      if (Nielsen.isPlayerBuffering()) {
        // Position is not sent while player is buffering
        return;
      }

      Nielsen.sendEvent('setPlayheadPosition', Nielsen.getPlayerPosition());
    };

    // First call
    sendPosition();

    // Then start interval
    Nielsen.pollingTimer = setInterval(sendPosition, POSITION_POLLING_INTERVAL);
  };

  static stopPolling: () => void = () => {
    if (Nielsen.pollingTimer) {
      clearInterval(Nielsen.pollingTimer);
      Nielsen.pollingTimer = null;
    }
  };

  static updateTimeshift: (value: number) => void = (value) => {
    if (!Nielsen.isEnabled || Nielsen.metadata === null) {
      return;
    }

    const {
      metadata: { cli_md: diffMode },
    } = Nielsen;

    let newDiffMode: MediametrieDiffusionMode | null = null;

    if (diffMode === (MediametrieDiffusionMode.Live as string) && value >= DOWNWARD_TIMESHIFT_THRESHOLD) {
      // Stop session because we're behind the live stream
      newDiffMode = MediametrieDiffusionMode.Timeshifting;
    } else if (diffMode === (MediametrieDiffusionMode.Timeshifting as string) && value <= UPWARD_TIMESHIFT_THRESHOLD) {
      // Stop session because we're not timeshifting anymore
      newDiffMode = MediametrieDiffusionMode.Live;
    }

    if (newDiffMode === null) {
      return;
    }

    Nielsen.notifyEnd();

    if (Nielsen.initialMetadata === null) {
      return;
    }

    Nielsen.metadata = {
      ...Nielsen.initialMetadata,
      cli_md: newDiffMode as string,
    };
    Nielsen.sendEvent('loadMetadata', Nielsen.metadata);

    Nielsen.notifyPlay();
  };

  /*
   * From Nielsen doc:
   * Call when content interrupted/paused and stop passing playhead position.
   * Do the same also when an Ad Break starts during the Content Playback.
   */
  static notifyStop: () => void = () => {
    if (!Nielsen.isEnabled || Nielsen.metadata === null || Nielsen.pollingTimer === null) {
      return;
    }

    Nielsen.stopPolling();
    Nielsen.sendEvent('stop', Nielsen.getPlayerPosition());
  };

  static notifyPlay: () => void = () => {
    if (!Nielsen.isEnabled || Nielsen.metadata === null) {
      return;
    }

    Nielsen.startPolling();
  };

  /*
   * From Nielsen doc:
   * Call when the current video asset completes playback and pass the playhead position.
   */
  static notifyEnd: () => void = () => {
    if (!Nielsen.isEnabled || Nielsen.metadata === null) {
      return;
    }

    Nielsen.stopPolling();
    Nielsen.sendEvent('end', Nielsen.getPlayerPosition());
    Nielsen.metadata = null;
  };

  static sendEvent: (eventName: string, value: number | NIELSEN_METRICS_PARAMS) => void = (eventName, value) => {
    if (!Nielsen.isEnabled || Nielsen.metadata === null) {
      return;
    }

    Nielsen.sdkInstance?.ggPM(eventName, value);
  };
}
